// ── Inventory ──────────────────────────────────────────────────────────────

/** Max length for EON waste log memo (DB + CrunchTime header); keep in sync with BE EonWasteLogEntries.memo. */
export const WASTE_MEMO_MAX_LENGTH = 40

/** Clamp memo while typing (UI state). */
export const clampWasteMemoInput = (value: string | undefined | null): string => {
  if (value == null) return ''

  return String(value).slice(0, WASTE_MEMO_MAX_LENGTH)
}

/** Trim and clamp for API / CrunchTime; `undefined` when empty after trim. */
export const normalizeWasteMemoForApi = (memo: string | undefined | null): string | undefined => {
  const t = memo != null ? String(memo).trim() : ''
  if (t === '') return undefined

  return t.length <= WASTE_MEMO_MAX_LENGTH ? t : t.slice(0, WASTE_MEMO_MAX_LENGTH)
}

/** Parse quantity from UI strings or API values (comma decimal, trim). Non-finite → 0. */
export const parseWasteQuantity = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  const s = String(value ?? '')
    .trim()
    .replace(/\uFF0E/g, '.')
    .replace(/\uFF0C/g, '.')
    .replace(/,/g, '.')
  const n = parseFloat(s)

  return Number.isFinite(n) ? n : 0
}

/** Round for CrunchTime payloads; matches Log Waste UI (max 2 decimal places for weight/volume). */
export const roundQuantityForCrunchTime = (quantity: unknown): number => {
  const q = parseWasteQuantity(quantity)
  if (!Number.isFinite(q)) return 0

  return Math.round(q * 100) / 100
}

export interface WasteDuplicateProductSummary {
  productId: string
  productName: string
  count: number
}

/** Products that appear on more than one pending publish line for the same date, category, and day part. */
export function findDuplicateWasteProductIds(
  rows: ReadonlyArray<{
    productId: string
    productName?: string | null
    transactionDate?: string | null
    category?: string | null
    dayPart?: string | null
  }>
): WasteDuplicateProductSummary[] {
  const byKey = new Map<string, { productName: string; count: number }>()
  for (const row of rows) {
    const productId = String(row.productId ?? '').trim()
    if (!productId) continue
    const transactionDate = String(row.transactionDate ?? '').trim()
    const category = String(row.category ?? '').trim()
    const dayPart = String(row.dayPart ?? '').trim()
    const key = `${transactionDate}|${productId}|${category}|${dayPart}`
    const existing = byKey.get(key)
    const productName = row.productName?.trim() || productId
    if (existing) {
      existing.count += 1
      if (existing.productName === productId && productName !== productId) {
        existing.productName = productName
      }
    } else {
      byKey.set(key, { productName, count: 1 })
    }
  }

  return [...byKey.entries()]
    .filter(([, v]) => v.count > 1)
    .map(([key, v]) => {
      const productId = key.split('|')[0] ?? key

      return { productId, productName: v.productName, count: v.count }
    })
    .sort((a, b) => a.productId.localeCompare(b.productId))
}

/** Trimmed storage code from adjustments / waste log; empty string when unknown (no default sent to CrunchTime). */
export const resolveInventoryStorageCode = (value: string | undefined | null): string => {
  const t = value != null ? String(value).trim() : ''

  return t
}

const matchesStorageOptionalProductPrefix = (upper: string): boolean => {
  if (upper.startsWith('B-') || upper.startsWith('W-')) return true
  if (/^[A-Z]{2}-B-/.test(upper) || /^[A-Z]{2}-W-/.test(upper)) return true

  return false
}

/**
 * Recipe / component-style CrunchTime product numbers (B- / W- or regional XX-B- / XX-W-).
 * Storage from adjustments / location pricing is not treated as mandatory for these before publish.
 */
export function isWasteProductStorageCodeOptional(productId: string, productName?: string): boolean {
  const id = String(productId ?? '')
    .trim()
    .toUpperCase()
  if (matchesStorageOptionalProductPrefix(id)) return true
  if (productName != null) {
    const n = String(productName).trim().toUpperCase()
    if (matchesStorageOptionalProductPrefix(n)) return true
  }

  return false
}

/**
 * Split lines by CrunchTime id: `P-*` (inventory / product adjustment) vs other prefixes (B-, W-, SR-, regional B/W, etc.).
 * Useful if a client groups calls by product style; the backend accepts one POST per line only.
 */
export function splitWasteInventoryLinesByProductPrefix<T extends { productId: string }>(
  lines: T[]
): {
  productAdjustmentLines: T[]
  recipeOrOtherLines: T[]
} {
  const productAdjustmentLines: T[] = []
  const recipeOrOtherLines: T[] = []
  for (const line of lines) {
    const id = String(line.productId ?? '')
      .trim()
      .toUpperCase()
    if (id.startsWith('P-')) productAdjustmentLines.push(line)
    else recipeOrOtherLines.push(line)
  }

  return { productAdjustmentLines, recipeOrOtherLines }
}

/**
 * Maps companyProductEnhancedUserDefinedCategoryDetails[0].code to CrunchTime recipe flag.
 * API-Product → false (inventory adjustment); API-Recipe → true (recipe).
 * If code is absent, uses `bucket`: `'inventory'` → false, `'recipe'` → true.
 */
export function resolveComponentRecipeAdjustmentFlagForWasteLine(
  userDefinedCategoryCode: string | undefined,
  bucket: 'inventory' | 'recipe'
): boolean {
  const c = String(userDefinedCategoryCode ?? '')
    .trim()
    .toUpperCase()
  if (c === 'API-Recipe') return true
  if (c === 'API-Product') return false

  return bucket === 'recipe'
}

/**
 * BFF header for POST /crunchtime/inventory and bulk recipe/adjustment routes (matches GET: false = inventory, true = recipe).
 * API-Product → "N". API-Recipe → "Y" (CrunchTime recipe flag).
 */
export function wasteInventoryHeaderFlagFromUdcCode(
  userDefinedCategoryCode: string | undefined
): 'Y' | 'N' | undefined {
  const c = String(userDefinedCategoryCode ?? '')
    .trim()
    .toUpperCase()
  if (c === 'API-PRODUCT') return 'N'
  if (c === 'API-RECIPE') return 'Y'

  return undefined
}

/** Matches backend recipe vs inventory prefix rules: recipe-style id → "Y", else "N". */
const REGION_RECIPE_PREFIX = /^[A-Z]{2}-(B|W)-/

export function wasteInventoryHeaderFlagFromProductId(productId: string): 'Y' | 'N' {
  const normalized = String(productId ?? '')
    .trim()
    .toUpperCase()
  if (normalized.startsWith('P-')) return 'N'
  if (normalized.startsWith('B-') || normalized.startsWith('W-') || normalized.startsWith('SR-')) {
    return 'Y'
  }
  if (REGION_RECIPE_PREFIX.test(normalized)) return 'Y'

  return 'N'
}

/** UDC when API-Product/API-Recipe; otherwise same rule as product id prefix (always Y or N). */
export function resolveWasteInventoryHeaderFlagLetter(
  productId: string,
  userDefinedCategoryCode: string | undefined
): 'Y' | 'N' {
  return (
    wasteInventoryHeaderFlagFromUdcCode(userDefinedCategoryCode) ?? wasteInventoryHeaderFlagFromProductId(productId)
  )
}

/** CrunchTime legacy recipe marker on GET headers; treat like "Y". */
export function isRecipeStyleCrunchTimeComponentFlag(value: string | undefined | null): boolean {
  const s = String(value ?? '')
    .trim()
    .toUpperCase()

  return s === 'Y' || s === 'P'
}

/** Active product / UDC row from CrunchTime (waste catalogue); "P" is legacy same as "Y". */
export function isCrunchTimeActiveYesFlag(value: string | undefined | null): boolean {
  const s = String(value ?? '')
    .trim()
    .toUpperCase()

  return s === 'Y' || s === 'P'
}

/**
 * UI label for which CrunchTime bulk path applies: Y → Recipe, N → inventory (product) adjustment.
 * Pass `userDefinedCategoryCode` from waste-products `adjustmentTypeCode` when available.
 */
export function getWasteInventoryTypeLabel(
  productId: string,
  userDefinedCategoryCode?: string
): 'Recipe' | 'Adjustment' {
  return resolveWasteInventoryHeaderFlagLetter(productId, userDefinedCategoryCode) === 'Y' ? 'Recipe' : 'Adjustment'
}

/**
 * Type column when showing rows from GET adjustments: prefer `inventoryAdjustmentHeaderDetails.componentRecipeAdjustmentFlag`
 * (CrunchTime may return legacy "P" for recipe). Falls back to product id + UDC when the header omits the flag.
 */
export function getWasteInventoryTypeLabelForRow(
  productId: string,
  userDefinedCategoryCode: string | undefined,
  crunchTimeHeaderComponentRecipeFlag?: string | null
): 'Recipe' | 'Adjustment' {
  const raw = crunchTimeHeaderComponentRecipeFlag != null ? String(crunchTimeHeaderComponentRecipeFlag).trim() : ''
  if (raw !== '') {
    const u = raw.toUpperCase()
    if (isRecipeStyleCrunchTimeComponentFlag(raw)) return 'Recipe'
    if (u === 'N') return 'Adjustment'
  }

  return getWasteInventoryTypeLabel(productId, userDefinedCategoryCode)
}

export interface CreateWasteEntryPayload {
  locationId: string
  productId: string /** CrunchTime product ID. Backend sets componentRecipeAdjustmentFlag: P-* → N; B-*, W-*, SR-*, or region codes like US-B-* → Y; optional userDefinedCategoryCode API-Product/API-Recipe overrides when header omits the flag. */
  quantity: number /** Quantity for this line. Use roundQuantityForCrunchTime before sending. */
  qualitativeJudgment: string
  transactionDate: string
  packageType?: string

  /** When set and non-empty, forwarded to CrunchTime; omitted when unknown. */
  storageCode?: string

  /** Sent on waste inventory bulk POSTs: optional memo on header; componentRecipeAdjustmentFlag always "Y" or "N". */
  header?: {
    daypartCode?: number
    memo?: string
    componentRecipeAdjustmentFlag: 'Y' | 'N'
  }

  /** When known: first UDC code from waste-products; refines Y/N with resolveWasteInventoryHeaderFlagLetter. */
  userDefinedCategoryCode?: string
}

/** Reason code → display label for CrunchTime qualitativeJudgment. */
export const WASTE_REASON_CODE_TO_LABEL: Record<string, string> = {
  'product-discontinued': 'Product Discontinued',
  'out-of-date-hold-time': 'Out of Date / Hold Time',
  'waste-end-of-day-day-part': 'Waste - End of Day/Day Part',
  'awaiting-credit-note-pfd': 'Awaiting Credit Note - PFD',
  'awaiting-credit-note-baiada': 'Awaiting Credit Note - Baiada',
  'awaiting-credit-note-morco': 'Awaiting Credit Note - Morco',
  'awaiting-credit-note-other': 'Awaiting Credit Note - Other'
}

export interface WasteSubmissionEntry {
  crunchTimeProductId: string
  quantity: number /** Quantity for the line; sent to CrunchTime. */
  /** Reason code; mapped to display label and sent as qualitativeJudgment (required for publish). */
  reason: string
  packageType?: string /** Package type (unit of measure) from inventoryUnitPackageType, forwarded to CrunchTime — e.g. "KGS", "Each", "Litres". */
  storageCode?: string /** Storage location code forwarded to CrunchTime — e.g. "DRYSTR". */
  /** Optional free text; sent as header.memo only (not qualitativeJudgment). */
  memo?: string
  userDefinedCategoryCode?: string /** companyProductEnhancedUserDefinedCategoryDetails[0].code — drives header.componentRecipeAdjustmentFlag with API-Product / API-Recipe when set. */
}

export interface SubmitWasteEntriesPayload {
  locationId: string
  entries: WasteSubmissionEntry[]
  transactionDate: string
}

/** Payload for saving a single day's waste entries to CrunchTime (Save button). */
export interface SaveWasteDayPayload {
  locationId: string
  date: string
  entries: WasteSubmissionEntry[]
}

/** One day's worth of entries to include in a period Publish. */
export interface PublishDayEntry {
  date: string
  entries: WasteSubmissionEntry[]
}

/** Payload for publishing all unsaved days in the current period (Publish button). */
export interface PublishWastePayload {
  locationId: string
  daysToPublish: PublishDayEntry[]

  /** Optional daypart code for CrunchTime header. */
  daypartCode?: number

  /** EonWasteLogEntry ids to mark as Published after successful CrunchTime publish. */
  publishedEntryIds?: number[]
}

// ── EON Waste Log (backend DB + CrunchTime) ────────────────────────────────

/** Single entry from GET /eon/waste-log. dayPart is stored in DB for easier summary display; fallback from loggedAt for older rows. */
export interface WasteLogEntryResponse {
  id: number
  restaurantId: number
  locationCode: string
  transactionDate: string
  productId: string
  productName: string
  category: string

  /** Numeric in DB; GET /eon/waste-log returns a string (e.g. "0.40") so JSON preserves 2 dp for weight/volume. */
  quantity: number | string
  reason?: string | null

  /** Optional memo persisted on the row; sent to CrunchTime only when non-empty (no reason fallback). */
  memo?: string | null
  loggedAt: string
  dayPart?: 'AM' | 'PM' | 'Overnight' | null /** AM, PM, or Overnight — stored per record for summary display. */
  packageType?: string | null
  storageCode?: string | null
  status?:
    | 'Pending'
    | 'Published'
    | null /** Pending until published to CrunchTime; then Published. Published entries are not editable. */
  /** 1 while visible. 0 when Published (after publish) or soft-deleted from manager summary (quantity 0). */
  isActive?: 0 | 1 | null
}

/** Period for summary edit: backend sets loggedAt to a time within this period on transactionDate. */
export type WasteLogPeriod = 'AM' | 'PM' | 'Overnight'

/** Payload for POST /eon/waste-log (save quantity + reason per product; backend sets loggedAt for day-part tracking). */
export interface SaveWasteLogEntryPayload {
  productId: string
  productName: string
  category: string
  quantity: number
  reason?: string

  /** Optional memo persisted on the row. */
  memo?: string
  period?: WasteLogPeriod
  packageType?: string
  storageCode?: string
}

export interface SaveWasteLogPayload {
  restaurantId: number
  locationCode: string
  date: string
  entries: SaveWasteLogEntryPayload[]
}

/** Payload for PATCH /eon/waste-log (Manager Edit: update existing records by id). quantity 0 soft-deletes (isActive/deletedAt only; DB quantity/reason/memo kept). */
export interface UpdateWasteLogPayload {
  updates: Array<{ id: number; quantity: number; reason?: string; memo?: string }>
}

export interface FetchWasteAdjustmentsParams {
  locationCode: string
  entryDate?: string
  includeNull?: boolean
  entryUser?: string
}

export interface WasteAdjustmentsResponse {
  success: boolean
  count: number
  data: CrunchTimeInventoryAdjustment[]
}

export interface CrunchTimeAdjustmentHeader {
  transactionNumber?: string
  transactionDate?: string
  locationCode?: string
  entryUser?: string
  memo?: string
  componentRecipeAdjustmentFlag?: string
  daypartCode?: number
  [key: string]: unknown
}

export interface CrunchTimeAdjustmentDetail {
  productNumber?: string | number
  productName?: string
  quantity?: number
  qualitativeJudgment?: string
  transactionDate?: string
  packageType?: string
  storageCode?: string
  lotNumber?: string
  recipeName?: string
  recipeQuantity?: number
  extendedCost?: number
  [key: string]: unknown
}

export interface CrunchTimeInventoryAdjustment {
  inventoryAdjustmentHeaderDetails?: CrunchTimeAdjustmentHeader
  inventoryAdjustmentDetailDetails?: CrunchTimeAdjustmentDetail[]
  inventoryValueAdjustmentHeaderDetails?: Record<string, unknown>
  inventoryValueAdjustmentDetailDetails?: Record<string, unknown>[]
}

// CrunchTime GET: axios `response.data` is `{ data: … }` then often `{ data: T, _metadata }`.
// Use `parseCrunchTimeAxiosResponse` from `@/src/utils/crunchTimeApiResponse`.

// ── Locations ──────────────────────────────────────────────────────────────

export interface FetchLocationsParams {
  activeFlag?: boolean
  locationCode?: string
}

export interface CrunchTimeLocation {
  [key: string]: unknown
}

export interface LocationsResponse {
  success: boolean
  count: number
  data: CrunchTimeLocation[]
  filter: Record<string, unknown> | null
}

// ── Recipes ────────────────────────────────────────────────────────────────

export interface FetchRecipesParams {
  componentDetails?: boolean
  includeDetails?: boolean
  productNumber?: string
  recipeName?: string
  category?: string
}

export interface CrunchTimeRecipe {
  [key: string]: unknown
}

export interface RecipesResponse {
  success: boolean
  count: number
  data: CrunchTimeRecipe[]
}

// ── Package Types ──────────────────────────────────────────────────────────

export interface CrunchTimePackageType {
  [key: string]: unknown
}

export interface PackageTypesResponse {
  success: boolean
  count: number
  data: CrunchTimePackageType[]
}

// ── Application Users ──────────────────────────────────────────────────────

export interface CrunchTimeApplicationUser {
  [key: string]: unknown
}

export interface ApplicationUsersResponse {
  success: boolean
  count: number
  data: CrunchTimeApplicationUser[]
}

// ── Company Products ────────────────────────────────────────────────────────

export interface FetchCompanyProductsParams {
  productNumber?: string
  productName?: string
  categoryName?: string
  subcategoryName?: string /** Filter to a subcategory (e.g. 'Waste Items'). */
  microcategoryName?: string /** CrunchTime microcategory; not used for waste logging (use GET /crunchtime/waste-products with concept + userDefinedCategory). */
  includeDetails?: boolean
  activeFlag?: boolean
  includeNull?: boolean
}

/** Core header fields for a Company Product Enhanced V1 record. */
export interface CrunchTimeCompanyProductHeader {
  activeFlag: string /** 'Y' = active, 'N' = inactive */
  name: string /** Product display name */
  number: string /** Product number - used as the CrunchTime product ID for inventory posting */
  categoryName?: string
  subcategoryName?: string
  microcategoryName?: string /** CrunchTime field; waste logging uses concept + userDefinedCategory (GET /crunchtime/waste-products). */
  inventoryUnitPackageType?: string /** Unit of measure for inventory (e.g. 'KGS', 'Each', 'Litres') */
  [key: string]: unknown
}

export interface CrunchTimeCompanyProduct {
  companyProductEnhancedHeaderDetails: CrunchTimeCompanyProductHeader
  [key: string]: unknown
}

export interface CompanyProductsResponse {
  success: boolean
  count: number
  data: CrunchTimeCompanyProduct[]
}

// ── Waste products (GET /crunchtime/waste-products) ─────────────────────────

/** Single item from GET /crunchtime/waste-products (concept + UDC aggregation). */
export interface WasteProductItem {
  productNumber: string
  name: string
  wasteGroupCode?: string
  wasteGroupName?: string

  /** Active API-* UDC code (API-Product / API-Recipe). */
  adjustmentTypeCode?: string
  packageType?: string
  activeFlag: string
}

/** Response from GET /crunchtime/waste-products. Same productNumber may repeat when mapped to multiple groups. */
export interface GetWasteProductsResponse {
  concept: string
  data: WasteProductItem[]
}
